import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ActiveState, ActiveStateLabel, ActiveStateOrder } from "@common/ADAPT.Common.Model/activeEntity.interface";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { Role, RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { PersonBreezeModel } from "@common/ADAPT.Common.Model/person/person";
import { IBreezeModel } from "@common/lib/data/breeze-model.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ObjectUtilities } from "@common/lib/utilities/object-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DateFormats } from "@common/ux/date-formats";
import { ContentReadyEvent, CustomSummaryInfo, InitializedEvent, RowCollapsedEvent, RowExpandedEvent } from "devextreme/ui/data_grid";
import { DxDataGridComponent } from "devextreme-angular";
import isEqual from "lodash.isequal";
import moment from "moment";
import { lastValueFrom } from "rxjs";
import { IntegratedArchitectureFrameworkService } from "../integrated-architecture-framework.service";

@Component({
    selector: "adapt-edit-role-connections",
    templateUrl: "./edit-role-connections.component.html",
    styleUrls: ["./edit-role-connections.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EditRoleConnectionsComponent extends BaseComponent implements OnInit {
    public readonly ShortDate = DateFormats.globalize.short;
    private readonly inactiveGroup = [ActiveStateLabel[ActiveState.Inactive]];

    @ViewChild(DxDataGridComponent) public dataGrid?: DxDataGridComponent;

    @Output() public roleConnectionCreated = new EventEmitter<RoleConnection>();
    @Output() public roleConnectionChanged = new EventEmitter<RoleConnection>();
    @Output() public roleConnectionDeletion = new EventEmitter<RoleConnection>();

    @Input() public role?: Role;
    @Input() public connection?: Connection;

    public showHistoric = false;
    public model?: IBreezeModel;
    public team?: Team;
    public entity?: Connection | Role;

    public addedRoleConnections = new Set<RoleConnection>();

    private userToggledInactiveGroup = false;

    constructor(
        private commonDataService: CommonDataService,
        private integratedArchitectureFrameworkService: IntegratedArchitectureFrameworkService,
    ) {
        super();
    }

    public get hasIncompleteRoleConnections() {
        return this.entity?.roleConnections.some((rc) => this.isIncludedAndIncomplete(rc));
    }

    public async ngOnInit() {
        this.entity = this.connection || this.role;

        await this.setAdditionalSettings();
    }

    public onGridInitialised(e: InitializedEvent) {
        // hide system and unadded role connections from the grid
        e.component?.filter((roleConnection: RoleConnection) => this.isIncluded(roleConnection)
            && !this.addedRoleConnections.has(roleConnection));
    }

    public async onGridContentReady(e: ContentReadyEvent) {
        // collapse Inactive by default
        // this gets called every time the grid updates so we need to keep track
        // if the user was the one who expanded the row
        if (!this.userToggledInactiveGroup) {
            if (this.showHistoric) {
                e.component.expandRow(this.inactiveGroup);
            } else {
                e.component.collapseRow(this.inactiveGroup);
            }
        }

        this.userToggledInactiveGroup = false;
    }

    public onGridRowExpanded(event: RowExpandedEvent) {
        if (isEqual(event.key, this.inactiveGroup)) {
            this.showHistoric = true;
            this.userToggledInactiveGroup = true;
        }
    }

    public onGridRowCollapsed(event: RowCollapsedEvent) {
        if (isEqual(event.key, this.inactiveGroup)) {
            this.showHistoric = false;
            this.userToggledInactiveGroup = true;
        }
    }

    public calcCustomSummary(info: CustomSummaryInfo) {
        if (info.name === "activeStateCustomSummary") {
            if (info.summaryProcess === "calculate") {
                const row: RoleConnection = info.value;
                info.totalValue = row.entityAspect.entityState.isAdded()
                    ? -1
                    : ActiveStateOrder.indexOf(row.activeState);
            }
        }
    }

    public calculateActiveCellValue(roleConnection: RoleConnection) {
        if (!roleConnection.roleConnectionId) {
            return;
        }

        return roleConnection.entityAspect.entityState.isAdded()
            ? "New"
            : ActiveStateLabel[roleConnection.activeState];
    }

    private async setAdditionalSettings() {
        this.model = this.connection
            ? RoleBreezeModel
            : PersonBreezeModel;

        this.showHistoric = false;

        if (!this.entity!.roleConnections.some((rc) => this.isIncluded(rc))) {
            await this.promiseToAddRoleConnection();
        } else if (!this.entity!.roleConnections.some((rc) => this.isIncludedAndNotHistoric(rc))) {
            // no current role but there are some historic ones - show the historic one instead
            this.showHistoric = true;
        }
    }

    public async promiseToAddRoleConnection() {
        const defaults: Partial<RoleConnection> = {
            role: this.role,
            connection: this.connection,
            team: this.team,
            startDate: this.connection?.startDate ?? new Date(),
        };

        const roleConnection = await lastValueFrom(this.integratedArchitectureFrameworkService.createRoleConnection(defaults));

        this.addedRoleConnections.add(roleConnection);
        this.roleConnectionCreated.emit(roleConnection);
    }

    public async onRoleConnectionChanged(roleConnection: RoleConnection) {
        if (roleConnection && roleConnection.endDate && moment.utc().isAfter(roleConnection.endDate)) {
            // most probably from just ending a role -> should expand the previous roles to indicate
            // where that has gone to
            this.showHistoric = true;
            this.userToggledInactiveGroup = false;
        }

        if (roleConnection.entityAspect.entityState.isDeleted() || roleConnection.entityAspect.entityState.isDetached()) {
            await this.onRoleConnectionDeletion(roleConnection);
        }

        this.roleConnectionChanged.emit(roleConnection);
        this.dataGrid?.instance.refresh();
    }

    public async onRoleConnectionDeletion(roleConnection: RoleConnection) {
        // perform deletion here instead of within edit-role-connection.component as that component will go away
        // causing unfinished emit chain based on timings
        await lastValueFrom(this.commonDataService.remove(roleConnection));

        this.roleConnectionDeletion.emit(roleConnection);
        this.addedRoleConnections.delete(roleConnection);

        // the filter we apply in onGridInitialised isn't re-evaluated since nothing in the entity changes here
        // so just tell the grid to refresh
        this.dataGrid?.instance.refresh();
    }

    public isIncludedAndNotHistoric(roleConnection: RoleConnection) {
        return this.isIncluded(roleConnection)
            && roleConnection.isNotHistoric();
    }

    public isIncludedAndIncomplete(roleConnection: RoleConnection) {
        return this.isIncluded(roleConnection)
            && (ObjectUtilities.isNullOrUndefined(roleConnection.role)
                || ObjectUtilities.isNullOrUndefined(roleConnection.connection));
    }

    public isIncluded(roleConnection: RoleConnection) {
        return this.filterTeam(roleConnection)
            && !this.isSystemRoleConnection(roleConnection);
    }

    private filterTeam(roleConnection: RoleConnection) {
        if (this.team) {
            return roleConnection.teamId === this.team.teamId;
        } else {
            return !roleConnection.teamId;
        }
    }

    private isSystemRoleConnection(roleConnection: RoleConnection) {
        return roleConnection.role
            && (roleConnection.role.extensions.isSystemAllocatedRole()
                || roleConnection.role.extensions.hasAccessPermissions());
    }
}
